import { AssistantCitation } from '@services/modules/AssistantStream'

import { RECORD_MARK } from './recordTokens'

/*
 * A record the answer names is opened from its name, where it is written: the label of a server
 * citation found in the text becomes a link to that citation's record. The text only decides
 * WHERE a link is shown; where it leads comes from the citation (type and id), never from what
 * the model wrote — the same rule as the pills under an answer, which now only list the records
 * the text does not name. Nothing here navigates: which records can be opened, and opening one,
 * are the caller's (see citationNavigation).
 */

/**
 * Characters markdown reads as syntax, and the starts of what GFM turns into a link by itself: a
 * label holding one may not arrive in the rendered text as it is written in the answer, so it is
 * left to its pill.
 */
const NOT_PLAIN_TEXT = /[*[\]<>`~|\\&@]|:\/\/|www\./i

/**
 * An underscore is plain text only between two letters or digits ("TEMPLATE_04B"): anywhere else
 * GFM may read it as emphasis.
 */
const EMPHASIS_UNDERSCORE = /(^|[^\p{L}\p{N}])_|_($|[^\p{L}\p{N}])/u

const LETTER_OR_DIGIT = /[\p{L}\p{N}]/u

const MIN_LABEL_LENGTH = 2

const escapeRegExp = (text: string) =>
  text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')

export type CanOpenCitation = (_citation: AssistantCitation) => boolean

/**
 * The citations a label in the text can stand for: a record with a screen to open, under a label
 * that is plain text and names that record alone (two records under one label are told apart by
 * their pills).
 */
const linkableCitations = (
  citations: AssistantCitation[],
  canOpen: CanOpenCitation
): AssistantCitation[] => {
  const byLabel = new Map<string, AssistantCitation[]>()
  for (const citation of citations) {
    const label = citation.label
    if (
      !label ||
      label !== label.trim() ||
      label.length < MIN_LABEL_LENGTH ||
      NOT_PLAIN_TEXT.test(label) ||
      EMPHASIS_UNDERSCORE.test(label) ||
      !canOpen(citation)
    ) {
      continue
    }
    byLabel.set(label, [...(byLabel.get(label) ?? []), citation])
  }
  return Array.from(byLabel.values())
    .filter(sameLabel => sameLabel.every(c => c.id === sameLabel[0].id))
    .map(sameLabel => sameLabel[0])
}

export interface LabelMatch {
  start: number
  end: number
  label: string
}

/**
 * Where the labels are written in a text, as whole words: "IM-12" is not found in "IM-123". The
 * longest label wins where two start at the same place.
 */
export const findCitationLabels = (
  text: string,
  labels: string[]
): LabelMatch[] => {
  if (!text || labels.length === 0) {
    return []
  }
  const pattern = new RegExp(
    [...labels]
      .sort((a, b) => b.length - a.length)
      .map(escapeRegExp)
      .join('|'),
    'g'
  )
  const matches: LabelMatch[] = []
  let match: RegExpExecArray | null
  while ((match = pattern.exec(text)) !== null) {
    const start = match.index
    const end = start + match[0].length
    const before = Array.from(text.slice(0, start)).pop() ?? ''
    const after = Array.from(text.slice(end))[0] ?? ''
    if (LETTER_OR_DIGIT.test(before) || LETTER_OR_DIGIT.test(after)) {
      // part of a longer word: look again from the next character
      pattern.lastIndex = start + 1
      continue
    }
    matches.push({ start, end, label: match[0] })
  }
  return matches
}

/** Code is shown as written: a label in it is not a link. */
const CODE =
  /^ {0,3}(`{3,}|~{3,})[^\n]*\n[\s\S]*?(?:^ {0,3}\1[^\n]*$|(?![\s\S]))|(`+)[\s\S]*?\2/gm

/** A record the user may not open: its name is only marked in the text, never a link or a pill. */
const isNameOnly = (citation: AssistantCitation) => citation.openable === false

/**
 * The citations of an answer, apart: those its text names and the user can open (shown as links
 * in the text), those its text names and the user may not open (shown in bold there), and the
 * rest the user can open (shown as pills under it).
 */
export const splitCitations = (
  content: string,
  citations: AssistantCitation[] | undefined,
  canOpen: CanOpenCitation
): {
  linked: AssistantCitation[]
  named: AssistantCitation[]
  rest: AssistantCitation[]
} => {
  const all = (citations ?? []).filter(c => !isNameOnly(c))
  const linkable = linkableCitations(all, canOpen)
  const markable = linkableCitations(
    (citations ?? []).filter(isNameOnly),
    () => true
  )
  if (linkable.length === 0 && markable.length === 0) {
    return { linked: [], named: [], rest: all }
  }
  const found = new Set(
    findCitationLabels(
      (content ?? '').replace(CODE, ' '),
      [...linkable, ...markable].map(c => c.label)
    ).map(m => m.label)
  )
  const linked = linkable.filter(c => found.has(c.label))
  const linkedLabels = new Set(linked.map(c => c.label))
  const named = markable.filter(
    c => found.has(c.label) && !linkedLabels.has(c.label)
  )
  return {
    linked,
    named,
    rest: all.filter(c => !linkedLabels.has(c.label)),
  }
}

/** The element a label is rendered as; AssistantMarkdown gives it its behaviour. */
export const CITATION_LINK_TAG = 'cite'

/** Markdown nodes whose text is not looked at: code, raw HTML, and what is never shown. */
const SKIPPED_NODES: ReadonlySet<string> = new Set([
  'code',
  'inlineCode',
  'html',
  'definition',
  'image',
  'imageReference',
  'citationLink',
])

const splitText = (value: string, labels: string[]): any[] => {
  const matches = findCitationLabels(value, labels)
  if (matches.length === 0) {
    return [{ type: 'text', value }]
  }
  const nodes: any[] = []
  let position = 0
  for (const { start, end, label } of matches) {
    if (start > position) {
      nodes.push({ type: 'text', value: value.slice(position, start) })
    }
    nodes.push({
      type: 'citationLink',
      data: { hName: CITATION_LINK_TAG },
      children: [{ type: 'text', value: label }],
    })
    position = end
  }
  if (position < value.length) {
    nodes.push({ type: 'text', value: value.slice(position) })
  }
  return nodes
}

const linkLabels = (node: any, labels: string[]) => {
  if (!Array.isArray(node.children)) {
    return
  }
  node.children = node.children.flatMap((child: any) => {
    if (child.type === 'text') {
      return splitText(child.value ?? '', labels)
    }
    if (!SKIPPED_NODES.has(child.type)) {
      linkLabels(child, labels)
    }
    return [child]
  })
}

/** The attribute a record ref's element carries: the id of the citation it opens. */
export const CITATION_ID_ATTRIBUTE = 'data-citation-id'

/** Text with record ref marks (recordTokens): each mark as its record's name, by the citation's id. */
const splitRecordMarks = (
  value: string,
  recordLabels: Record<string, string>
): any[] | null => {
  const marks = Array.from(value.matchAll(RECORD_MARK))
  if (marks.length === 0) {
    return null
  }
  const nodes: any[] = []
  let position = 0
  for (const match of marks) {
    const start = match.index ?? 0
    if (start > position) {
      nodes.push({ type: 'text', value: value.slice(position, start) })
    }
    const label = recordLabels[match[1]]
    if (label) {
      nodes.push({
        type: 'citationLink',
        data: {
          hName: CITATION_LINK_TAG,
          hProperties: { dataCitationId: match[1] },
        },
        children: [{ type: 'text', value: label }],
      })
    }
    position = start + match[0].length
  }
  if (position < value.length) {
    nodes.push({ type: 'text', value: value.slice(position) })
  }
  return nodes
}

const linkRecordMarks = (node: any, recordLabels: Record<string, string>) => {
  if (!Array.isArray(node.children)) {
    return
  }
  node.children = node.children.flatMap((child: any) => {
    if (child.type === 'text') {
      return splitRecordMarks(child.value ?? '', recordLabels) ?? [child]
    }
    if (!SKIPPED_NODES.has(child.type)) {
      linkRecordMarks(child, recordLabels)
    }
    return [child]
  })
}

export interface CitationLinkOptions {
  /** Labels found in the text (answers written before record refs). */
  labels?: string[]
  /** Record ref marks: citation id → the record's name. */
  recordLabels?: Record<string, string>
}

/**
 * remark plugin: wraps each record ref (by its citation's id) and then each label written in the
 * text in a CITATION_LINK_TAG element.
 */
export const remarkCitationLinks =
  ({ labels, recordLabels }: CitationLinkOptions = {}) =>
  (tree: any) => {
    if (recordLabels && Object.keys(recordLabels).length) {
      linkRecordMarks(tree, recordLabels)
    }
    if (labels?.length) {
      linkLabels(tree, labels)
    }
  }
